// Design System FilterBar Component
// Unified filter bar with search, filter, and sort functionality

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Chip,
  Stack,
  Collapse,
  useTheme,
  styled,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { spacingTokens, borderTokens, shadowTokens, transitionTokens } from '../../tokens';
import Button from '../core/Button';

// Styled components
const StyledFilterContainer = styled(Box)(({ theme }) => ({
  marginBottom: spacingTokens.layout.md,
}));

const StyledSearchField = styled(TextField)(({ theme, magical }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: borderTokens.full,
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.background.paper, 0.6)
      : theme.palette.background.paper,
    transition: transitionTokens.smooth,
    
    ...(magical && {
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
      '&:hover': {
        boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.15)}`,
      },
      '&.Mui-focused': {
        boxShadow: `0 0 30px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    }),
    
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    
    '& .MuiInputBase-input': {
      fontSize: '0.925rem',
    },
  },
}));

const StyledFilterChip = styled(Chip)(({ theme }) => ({
  borderRadius: borderTokens.full,
  height: 28,
  transition: transitionTokens.smooth,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    transform: 'translateY(-1px)',
    boxShadow: shadowTokens.small,
  },
  
  '& .MuiChip-deleteIcon': {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
}));

/**
 * FilterBar - Unified filter bar component
 * 
 * Features:
 * - Search with debouncing
 * - Filter button with badge
 * - Sort button
 * - Active filter chips
 * - Customizable actions
 * - Magical mode
 * - Responsive design
 * 
 * @example
 * // Basic usage
 * <FilterBar
 *   searchValue={search}
 *   onSearchChange={setSearch}
 *   onFilterClick={handleFilterOpen}
 *   onSortClick={handleSortOpen}
 *   hasActiveFilters={filters.length > 0}
 * />
 * 
 * // With active filters and custom actions
 * <FilterBar
 *   searchValue={search}
 *   onSearchChange={setSearch}
 *   onFilterClick={handleFilterOpen}
 *   onSortClick={handleSortOpen}
 *   activeFilters={[
 *     { id: '1', label: 'Status: Active', onDelete: () => removeFilter('status') },
 *     { id: '2', label: 'Type: Project', onDelete: () => removeFilter('type') }
 *   ]}
 *   actions={
 *     <Button variant="contained" startIcon={<AddIcon />}>
 *       Add New
 *     </Button>
 *   }
 * />
 */
const FilterBar = ({
  // Search props
  searchValue = '',
  onSearchChange,
  onSearchClear,
  searchPlaceholder = 'Search...',
  searchDebounce = 300,
  
  // Filter props
  onFilterClick,
  hasActiveFilters = false,
  activeFilters = [],
  onClearAllFilters,
  showFilterButton = true,
  
  // Sort props
  onSortClick,
  sortValue,
  sortDirection = 'asc',
  showSortButton = true,
  
  // Display props
  showActiveFilters = true,
  collapseFilters = false,
  
  // Style props
  magical = false,
  variant = 'outlined',
  size = 'small',
  
  // Custom content
  actions,
  startActions,
  additionalFilters,
  
  // Other props
  sx,
  ...props
}) => {
  const theme = useTheme();
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [filtersCollapsed, setFiltersCollapsed] = useState(collapseFilters);
  
  // Debounced search handler
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchValue) {
        onSearchChange?.(localSearch);
      }
    }, searchDebounce);
    
    return () => clearTimeout(timer);
  }, [localSearch, searchValue, onSearchChange, searchDebounce]);
  
  // Sync external search value
  React.useEffect(() => {
    if (searchValue !== localSearch) {
      setLocalSearch(searchValue);
    }
  }, [searchValue]);
  
  const handleSearchChange = (event) => {
    setLocalSearch(event.target.value);
  };
  
  const handleSearchClear = () => {
    setLocalSearch('');
    onSearchChange?.('');
    onSearchClear?.();
  };
  
  // Calculate if we have any active filters
  const hasFilters = hasActiveFilters || activeFilters.length > 0 || searchValue;
  
  // Render active filter chips
  const renderActiveFilters = () => {
    if (!showActiveFilters || (!activeFilters.length && !searchValue)) {
      return null;
    }
    
    const chips = [];
    
    // Add search chip if there's a search term
    if (searchValue) {
      chips.push(
        <StyledFilterChip
          key="search"
          label={`Search: "${searchValue}"`}
          onDelete={handleSearchClear}
          size="small"
        />
      );
    }
    
    // Add filter chips
    activeFilters.forEach((filter) => {
      chips.push(
        <StyledFilterChip
          key={filter.id || filter.label}
          label={filter.label}
          onDelete={filter.onDelete}
          size="small"
          icon={filter.icon}
          color={filter.color}
        />
      );
    });
    
    // Add clear all button if multiple filters
    if (chips.length > 1 && onClearAllFilters) {
      chips.push(
        <Button
          key="clear-all"
          variant="text"
          size="small"
          onClick={onClearAllFilters}
          sx={{ ml: 1 }}
        >
          Clear All
        </Button>
      );
    }
    
    return (
      <Collapse in={!filtersCollapsed && chips.length > 0}>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ 
            mt: 2,
            gap: 1,
          }}
        >
          {chips}
        </Stack>
      </Collapse>
    );
  };
  
  return (
    <StyledFilterContainer sx={sx} {...props}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {/* Start actions */}
        {startActions}
        
        {/* Search field */}
        <StyledSearchField
          value={localSearch}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
          size={size}
          variant={variant}
          magical={magical}
          fullWidth
          sx={{ 
            flex: 1,
            maxWidth: { sm: 400 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: localSearch && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleSearchClear}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        {/* Filter and sort buttons */}
        <Stack direction="row" spacing={1}>
          {showFilterButton && onFilterClick && (
            <Button
              variant="outlined"
              size={size}
              startIcon={<FilterListIcon />}
              onClick={onFilterClick}
              magical={magical}
            >
              Filter
              {hasFilters && (
                <Badge
                  color="primary"
                  variant="dot"
                  sx={{ ml: 1 }}
                />
              )}
            </Button>
          )}
          
          {showSortButton && onSortClick && (
            <Button
              variant="outlined"
              size={size}
              startIcon={<SortIcon />}
              onClick={onSortClick}
              magical={magical}
            >
              Sort
              {sortValue && (
                <Box
                  component="span"
                  sx={{ 
                    ml: 0.5,
                    fontSize: '0.75rem',
                    opacity: 0.7,
                  }}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Box>
              )}
            </Button>
          )}
          
          {/* Additional filter controls */}
          {additionalFilters}
        </Stack>
        
        {/* Custom actions */}
        {actions}
      </Stack>
      
      {/* Active filters display */}
      {renderActiveFilters()}
    </StyledFilterContainer>
  );
};

FilterBar.propTypes = {
  // Search props
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchClear: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  searchDebounce: PropTypes.number,
  
  // Filter props
  onFilterClick: PropTypes.func,
  hasActiveFilters: PropTypes.bool,
  activeFilters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    icon: PropTypes.node,
    color: PropTypes.string,
  })),
  onClearAllFilters: PropTypes.func,
  showFilterButton: PropTypes.bool,
  
  // Sort props
  onSortClick: PropTypes.func,
  sortValue: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  showSortButton: PropTypes.bool,
  
  // Display props
  showActiveFilters: PropTypes.bool,
  collapseFilters: PropTypes.bool,
  
  // Style props
  magical: PropTypes.bool,
  variant: PropTypes.oneOf(['outlined', 'filled', 'standard']),
  size: PropTypes.oneOf(['small', 'medium']),
  
  // Custom content
  actions: PropTypes.node,
  startActions: PropTypes.node,
  additionalFilters: PropTypes.node,
  
  sx: PropTypes.object,
};

export default FilterBar;